import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useRegisterSocketRooms } from '../hooks/useSocketInvalidation';
import { useConstituencies, useCampaigns, useDashboardStats } from '../hooks/queries';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Users, CheckCircle2, XCircle, Home, TrendingUp, Phone, UserCheck, Blocks, Maximize2, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell, PieChart, Pie } from 'recharts';

// Custom hook for debouncing
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const STATUS_COLORS = {
  Pledge: '#059669',
  Against: '#E11D48', 
  Undecided: '#D97706',
  'Meet Candidate': '#8B5CF6',
  NA: '#6B7280',
  WN: '#9CA3AF',
  SO: '#D1D5DB',
  OC: '#E5E7EB',
  Blank: '#F3F4F6'
};

const GENDER_COLORS = {
  Male: '#3B82F6',
  Female: '#EC4899',
  Other: '#8B5CF6',
  Unknown: '#9CA3AF'
};

// Improved Voting Day Countdown Widget
const VotingDayCountdown = ({ votingDay }) => {
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, isPast: false, isToday: false });
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!votingDay) return;

    const calculateCountdown = () => {
      const now = new Date();
      const target = new Date(votingDay);
      const diff = target - now;
      
      // Check if today
      const isToday = now.toDateString() === target.toDateString();

      if (diff <= 0 && !isToday) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true, isToday: false });
        return;
      }

      if (isToday) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, isPast: false, isToday: true });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds, isPast: false, isToday: false });
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);
    return () => clearInterval(interval);
  }, [votingDay]);

  if (!votingDay) return null;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const CountdownDisplay = ({ large = false }) => (
    <div className={`flex items-center justify-center gap-2 md:gap-4 ${large ? 'scale-150' : ''}`}>
      {[
        { value: countdown.days, label: 'Days' },
        { value: countdown.hours, label: 'Hours' },
        { value: countdown.minutes, label: 'Mins' },
        { value: countdown.seconds, label: 'Secs' }
      ].map((item, idx) => (
        <div key={idx} className={`text-center ${large ? 'min-w-[100px]' : 'min-w-[70px]'}`}>
          <div className={`bg-gradient-to-b from-slate-800 to-slate-900 rounded-xl shadow-lg ${large ? 'p-6' : 'p-3'}`}>
            <div className={`font-mono font-bold text-white ${large ? 'text-6xl' : 'text-3xl'}`}>
              {String(item.value).padStart(2, '0')}
            </div>
          </div>
          <div className={`mt-2 font-medium text-slate-600 uppercase tracking-wider ${large ? 'text-sm' : 'text-xs'}`}>
            {item.label}
          </div>
        </div>
      ))}
    </div>
  );

  // Fullscreen Modal
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-slate-100 to-slate-200 flex flex-col items-center justify-center">
        <Button 
          variant="ghost" 
          size="icon"
          className="absolute top-4 right-4"
          onClick={() => setIsFullscreen(false)}
        >
          <X className="h-6 w-6" />
        </Button>
        <h2 className="text-2xl font-semibold text-slate-600 mb-2">Voting Day</h2>
        <p className="text-lg text-slate-500 mb-8">{formatDate(votingDay)}</p>
        <CountdownDisplay large />
      </div>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-slate-800">Voting Day Countdown</h3>
                <p className="text-slate-500">{formatDate(votingDay)}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setIsFullscreen(true)}
                className="hidden md:flex"
              >
                <Maximize2 className="h-4 w-4 mr-2" />
                Fullscreen
              </Button>
            </div>
            
            {countdown.isToday ? (
              <div className="flex items-center justify-center py-4">
                <div className="px-8 py-4 bg-green-500 text-white rounded-xl text-2xl font-bold animate-pulse shadow-lg">
                  🗳️ VOTING DAY - LIVE
                </div>
              </div>
            ) : countdown.isPast ? (
              <div className="flex items-center justify-center py-4">
                <div className="flex items-center gap-3 px-6 py-3 bg-slate-200 rounded-xl">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                  <span className="text-lg font-semibold text-slate-700">Voting Completed</span>
                </div>
              </div>
            ) : (
              <div data-testid="countdown-widget">
                <CountdownDisplay />
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [selectedConstituency, setSelectedConstituency] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [majorityThresholdInput, setMajorityThresholdInput] = useState('20');

  const majorityThreshold = useDebounce(parseInt(majorityThresholdInput) || 1, 800);

  useRegisterSocketRooms(
    selectedConstituency ? [`dashboard:${selectedConstituency}`, `records:${selectedConstituency}`] : []
  );

  const { data: constituencies = [] } = useConstituencies();
  const { data: campaigns = [] } = useCampaigns(selectedConstituency);
  const { data: stats, isLoading: loading } = useDashboardStats(
    selectedConstituency,
    selectedCampaign,
    majorityThreshold
  );

  useEffect(() => {
    if (constituencies.length > 0 && !selectedConstituency) {
      setSelectedConstituency(constituencies[0].constituency_id);
    }
  }, [constituencies, selectedConstituency]);

  useEffect(() => {
    if (campaigns.length > 0 && selectedConstituency) {
      setSelectedCampaign(campaigns[0].campaign_id);
    } else if (campaigns.length === 0) {
      setSelectedCampaign(null);
    }
  }, [campaigns, selectedConstituency]);

  // Prepare Majority Constituents data for horizontal bar chart
  const majorityHousesData = useMemo(() => {
    if (!stats?.majority_houses) return [];
    return Object.entries(stats.majority_houses)
      .map(([house, data]) => ({
        house: house.length > 20 ? house.substring(0, 20) + '...' : house,
        fullHouse: house,
        total: data.total,
        reached: data.reached,
        unreached: data.unreached
      }))
      .sort((a, b) => b.total - a.total);
  }, [stats?.majority_houses]);

  // Reached vs Unreached data
  const reachedVsUnreachedData = stats ? [
    {
      name: 'Overall',
      Reached: stats.total_reached,
      Unreached: stats.total_unreached
    }
  ] : [];

  // Block stats data
  const blockStatsData = useMemo(() => {
    if (!stats?.block_stats) return [];
    return Object.entries(stats.block_stats)
      .map(([block, data]) => ({
        block: block,
        total: data.total,
        reached: data.reached,
        unreached: data.unreached,
        pledge: data.pledge,
        against: data.against,
        undecided: data.undecided
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 15);
  }, [stats?.block_stats]);

  // Gender stats data for pie chart
  const genderStatsData = useMemo(() => {
    if (!stats?.gender_stats) return [];
    return Object.entries(stats.gender_stats)
      .filter(([_, count]) => count > 0)
      .map(([gender, count]) => ({
        name: gender,
        value: count,
        color: GENDER_COLORS[gender] || '#6B7280'
      }));
  }, [stats?.gender_stats]);

  // Call Centre Performance data
  const callCentreData = useMemo(() => {
    if (!stats?.call_centre_stats) return [];
    return stats.call_centre_stats
      .sort((a, b) => b.updates_made - a.updates_made)
      .slice(0, 10);
  }, [stats?.call_centre_stats]);

  const candidateStatsArray = stats ? Object.values(stats.candidate_stats) : [];

  return (
    <Layout>
      <div className="p-4 md:p-8" data-testid="dashboard-page">
        <div className="mb-6">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-muted-foreground">Campaign performance and analytics</p>
        </div>

        {/* Selectors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <Label className="text-sm font-medium mb-2 block">Constituency</Label>
            <Select value={selectedConstituency || ''} onValueChange={setSelectedConstituency}>
              <SelectTrigger data-testid="constituency-select">
                <SelectValue placeholder="Select constituency" />
              </SelectTrigger>
              <SelectContent>
                {constituencies.map(c => (
                  <SelectItem key={c.constituency_id} value={c.constituency_id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Campaign</Label>
            <Select value={selectedCampaign || ''} onValueChange={setSelectedCampaign}>
              <SelectTrigger data-testid="campaign-select">
                <SelectValue placeholder="Select campaign" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map(c => (
                  <SelectItem key={c.campaign_id} value={c.campaign_id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : !stats ? (
          <Card>
            <CardContent className="py-12 text-center">
              <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Select constituency and campaign to view stats</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Voting Day Countdown Widget */}
            {stats.voting_day && (
              <div className="mb-6">
                <VotingDayCountdown votingDay={stats.voting_day} />
              </div>
            )}

            {/* Overview Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">Total Constituents</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats.total_records.toLocaleString()}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">Reached</CardTitle>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">{stats.total_reached.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">
                    {stats.total_records > 0 ? ((stats.total_reached / stats.total_records) * 100).toFixed(1) : 0}%
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">Unreached</CardTitle>
                  <XCircle className="h-4 w-4 text-gray-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-500">{stats.total_unreached.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">
                    {stats.total_records > 0 ? ((stats.total_unreached / stats.total_records) * 100).toFixed(1) : 0}%
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">Total Houses</CardTitle>
                  <Home className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {stats.all_houses ? Object.keys(stats.all_houses).length.toLocaleString() : 0}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Status Breakdown Cards - Only show specific statuses */}
            <div className="grid grid-cols-5 gap-2 mb-6">
              {['Pledge', 'Against', 'Undecided', 'Meet Candidate', 'Blank'].map((status) => {
                const count = stats.status_breakdown?.[status] || 0;
                return (
                  <Card key={status} className="p-2">
                    <div className="text-center">
                      <div 
                        className="text-xl font-bold" 
                        style={{ color: STATUS_COLORS[status] || '#6B7280' }}
                      >
                        {count.toLocaleString()}
                      </div>
                      <div className="text-xs text-muted-foreground">{status}</div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Majority Constituents Chart */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <CardTitle className="flex items-center gap-2">
                      <Home className="h-5 w-5" />
                      Majority Constituents
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm whitespace-nowrap">Min. Constituents:</Label>
                      <Input
                        type="number"
                        min="1"
                        value={majorityThresholdInput}
                        onChange={(e) => setMajorityThresholdInput(e.target.value)}
                        className="w-20 h-8"
                        data-testid="majority-threshold-input"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Showing {majorityHousesData.length} houses with {majorityThreshold}+ constituents
                  </p>
                </CardHeader>
                <CardContent>
                  {majorityHousesData.length > 0 ? (
                    <div style={{ height: Math.max(350, majorityHousesData.length * 35) }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart 
                          data={majorityHousesData} 
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis 
                            type="category" 
                            dataKey="house" 
                            width={120}
                            tick={{ fontSize: 11 }}
                          />
                          <Tooltip 
                            formatter={(value, name) => [value.toLocaleString(), name]}
                            labelFormatter={(label, payload) => payload[0]?.payload?.fullHouse || label}
                          />
                          <Legend />
                          <Bar dataKey="reached" fill="#059669" name="Reached" stackId="a" />
                          <Bar dataKey="unreached" fill="#9CA3AF" name="Unreached" stackId="a" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                      No houses with {majorityThreshold}+ constituents found
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Reached vs Unreached Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" />
                    Reached vs Unreached
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart 
                      data={reachedVsUnreachedData} 
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" hide />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Reached" fill="#059669" stackId="a" radius={[0, 0, 0, 0]}>
                        {reachedVsUnreachedData.map((entry, index) => (
                          <Cell key={`cell-reached-${index}`} />
                        ))}
                      </Bar>
                      <Bar dataKey="Unreached" fill="#9CA3AF" stackId="a" radius={[0, 4, 4, 0]}>
                        {reachedVsUnreachedData.map((entry, index) => (
                          <Cell key={`cell-unreached-${index}`} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  
                  {/* Progress bar style display */}
                  <div className="mt-4 px-4">
                    <div className="flex h-8 rounded-lg overflow-hidden">
                      <div 
                        className="bg-green-600 flex items-center justify-center text-white text-sm font-medium"
                        style={{ width: `${stats.total_records > 0 ? (stats.total_reached / stats.total_records) * 100 : 0}%` }}
                      >
                        {stats.total_reached > 0 && stats.total_reached.toLocaleString()}
                      </div>
                      <div 
                        className="bg-gray-400 flex items-center justify-center text-white text-sm font-medium"
                        style={{ width: `${stats.total_records > 0 ? (stats.total_unreached / stats.total_records) * 100 : 0}%` }}
                      >
                        {stats.total_unreached > 0 && stats.total_unreached.toLocaleString()}
                      </div>
                    </div>
                    <div className="flex justify-between mt-2 text-sm">
                      <span className="text-green-600 font-medium">
                        Reached: {stats.total_records > 0 ? ((stats.total_reached / stats.total_records) * 100).toFixed(1) : 0}%
                      </span>
                      <span className="text-gray-500 font-medium">
                        Unreached: {stats.total_records > 0 ? ((stats.total_unreached / stats.total_records) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 2 - New Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Constituents by Block */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Blocks className="h-5 w-5" />
                    Constituents by Block
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {blockStatsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={blockStatsData} margin={{ top: 5, right: 30, left: 20, bottom: 50 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="block" 
                          angle={-45} 
                          textAnchor="end" 
                          height={60}
                          tick={{ fontSize: 10 }}
                        />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="reached" fill="#059669" name="Reached" stackId="a" />
                        <Bar dataKey="unreached" fill="#9CA3AF" name="Unreached" stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                      No block data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Constituents by Gender */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCheck className="h-5 w-5" />
                    Constituents by Gender
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {genderStatsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={genderStatsData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {genderStatsData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => value.toLocaleString()} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                      No gender data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 3 - Pledge Status by Block */}
            <div className="grid grid-cols-1 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Blocks className="h-5 w-5" />
                    Pledge Status by Block
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {blockStatsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={blockStatsData} margin={{ top: 5, right: 30, left: 20, bottom: 50 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="block" 
                          angle={-45} 
                          textAnchor="end" 
                          height={60}
                          tick={{ fontSize: 10 }}
                        />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="pledge" fill="#059669" name="Pledge" />
                        <Bar dataKey="against" fill="#E11D48" name="Against" />
                        <Bar dataKey="undecided" fill="#D97706" name="Undecided" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                      No block data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Call Centre Performance */}
            {callCentreData.length > 0 && (
              <div className="mb-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="h-5 w-5" />
                      Call Centre Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-4">Caller</th>
                            <th className="text-center py-2 px-4">Updates Made</th>
                            <th className="text-center py-2 px-4">Blocks Assigned</th>
                            <th className="text-center py-2 px-4">Blocks Completed</th>
                            <th className="text-center py-2 px-4">Progress</th>
                          </tr>
                        </thead>
                        <tbody>
                          {callCentreData.map((caller, idx) => (
                            <tr key={idx} className="border-b hover:bg-muted/30">
                              <td className="py-3 px-4 font-medium">{caller.caller_name}</td>
                              <td className="py-3 px-4 text-center">{caller.updates_made.toLocaleString()}</td>
                              <td className="py-3 px-4 text-center">{caller.blocks_assigned}</td>
                              <td className="py-3 px-4 text-center">{caller.blocks_completed}</td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-primary rounded-full"
                                      style={{ 
                                        width: caller.blocks_assigned > 0 
                                          ? `${(caller.blocks_completed / caller.blocks_assigned) * 100}%` 
                                          : '0%' 
                                      }}
                                    />
                                  </div>
                                  <span className="text-xs text-muted-foreground w-12 text-right">
                                    {caller.blocks_assigned > 0 
                                      ? `${Math.round((caller.blocks_completed / caller.blocks_assigned) * 100)}%`
                                      : '0%'}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Candidate Stats */}
            {candidateStatsArray.length > 0 && (
              <div className="mb-6">
                <h2 className="text-2xl font-semibold mb-4">Candidate Performance</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {candidateStatsArray.map((candidate, idx) => (
                    <Card key={idx}>
                      <CardHeader>
                        <CardTitle className="text-lg">{candidate.candidate_name}</CardTitle>
                        <p className="text-sm text-muted-foreground">{candidate.candidate_role}</p>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Reached:</span>
                          <span className="font-semibold">{candidate.reached.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-green-600">Pledge:</span>
                          <span className="font-semibold text-green-600">{candidate.pledge.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-red-600">Against:</span>
                          <span className="font-semibold text-red-600">{candidate.against.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-amber-600">Undecided:</span>
                          <span className="font-semibold text-amber-600">{candidate.undecided.toLocaleString()}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
